import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle,
  Loader2,
  RotateCcw,
  Trash2,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function bytesLabel(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export default function UnfinalizedRecordingsWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyRoomId, setBusyRoomId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/recording-unfinalized");
      setRows(data?.unfinalized || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/recording-unfinalized");
        if (!cancelled) setRows(data?.unfinalized || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const finalize = async (row) => {
    setBusyRoomId(row.room_id);
    try {
      const fd = new FormData();
      fd.append("duration_ms", "0");
      const { data } = await api.post(
        `/live-rooms/${row.room_id}/recording/finalize`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `Recovered · ${data?.chunks_combined || row.chunk_count} chunks stitched`
      );
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Recovery failed"
      );
    } finally {
      setBusyRoomId(null);
    }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div
      data-testid="unfinalized-recordings-widget"
      className="mt-6 border border-rose-200 bg-rose-50"
    >
      <div className="flex items-center gap-2 border-b border-rose-200 bg-rose-100 px-5 py-3">
        <AlertTriangle className="h-4 w-4 text-rose-700" />
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-rose-900">
            Unfinalized recordings · {rows.length}
          </div>
          <div className="text-[11px] text-rose-800">
            Recordings whose chunks are on the server but never got stitched
            (browser crash / network drop). One click recovers them.
          </div>
        </div>
      </div>
      <ul className="divide-y divide-rose-100">
        {rows.map((row) => (
          <li
            key={row.room_id}
            data-testid={`unfinalized-row-${row.room_id}`}
            className="flex flex-wrap items-center justify-between gap-3 bg-white px-5 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Video className="h-3.5 w-3.5 text-rose-600" />
                {row.candidate_name || "Unknown candidate"}
              </div>
              <div className="text-[11px] text-slate-500">
                {row.candidate_email || "—"} · {row.chunk_count} chunk
                {row.chunk_count === 1 ? "" : "s"} · {bytesLabel(row.bytes)}{" "}
                · {timeAgo(row.oldest_chunk_at)}
              </div>
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                Room {row.room_id.slice(0, 8)}…
              </div>
            </div>
            <Button
              data-testid={`unfinalized-finalize-btn-${row.room_id}`}
              size="sm"
              onClick={() => finalize(row)}
              disabled={busyRoomId === row.room_id}
              className="bg-rose-600 text-white hover:bg-rose-500"
            >
              {busyRoomId === row.room_id ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              )}
              Recover
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
